import {
  Batcher,
  ExportResultCode,
  type BatcherOptions,
  type ExportResult,
} from './batcher'
import { createSampler } from './sampler'
import { _createMetricsFactory } from './tel-metrics'
import type { DeltaMetricPoint, GaugeMetricPoint } from './tel-metrics-types'
import type { TelErrorData } from './tel-error-data'
import {
  getApmDurationHistogram,
  getApmNormalizedDurationHistogram,
  getApmRequestCounter,
  type ApmCounter,
  type ApmHistogram,
} from './apm-metrics'
import type {
  BoundTelExporter,
  TelErrorEvent,
  TelExporter,
  TelStorage,
} from './tel-storage'
import { createTelStorage as createExporterTelStorage } from './tel-storage'
import {
  ALWAYS_EXPORT_LOG_ATTRIBUTE,
  formatAlwaysExportValue,
  shouldAlwaysExportLog,
} from './tel-types'
import type {
  LogData,
  SpanData,
  TelDeployResource,
  TelMetricResource,
  TraceData,
} from './tel-types'
import type {
  ClientErrorCreate,
  ClientErrorGroupChange,
  ClientErrorGroup,
  ClientErrorOccurrence,
  ClientErrorEvidenceReason,
  ClientStackResolver,
} from './client-error-groups'
import {
  createClientErrorGroupSeed,
  mergeClientErrorGroupChange,
} from './client-error-groups'
import { isProdEnv } from './env'

/**
 * Exporter behavior that is independent of how batches leave the process.
 * HTTP option types add transport config; custom transports use concern-specific
 * `*ExporterWithTransport` factories.
 */
export interface ExporterRetryOptions {
  maxAttempts?: number
  baseDelayMs?: number
  maxDelayMs?: number
}

export interface CoreHttpExporterOptions {
  batcherConfig?: BatcherOptions
  /** Trace-batch retry policy owned by runtime storage. */
  retry?: ExporterRetryOptions
  successfulTraceSampleRate?: number
  dangerouslyAllowProdSuccessfulTraceSampling?: boolean
  logSuccessfulExports?: boolean
  logPrefix?: string
  exportAllLogs?: boolean
  errorEvidenceTimeoutMs?: number
  /**
   * Derive request APM metrics from completed request spans.
   *
   * Server exporter only. Uses normal metric export path. Enabled by default;
   * pass `false` to disable. Latency regression alerting is also enabled by
   * default with APM; pass `{ alerting: false }` to disable alerts only.
   */
  apm?: boolean | { alerting?: boolean | ApmAlertingOptions }
}

export interface HttpTransportOptions {
  url: string
  headers?: Record<string, string>
  timeoutMs?: number
  retry?: ExporterRetryOptions
  fetch?: typeof fetch
}

export type HttpErrorTrackingExporterOptions = HttpTransportOptions &
  Pick<
    CoreHttpExporterOptions,
    | 'batcherConfig'
    | 'retry'
    | 'logSuccessfulExports'
    | 'logPrefix'
    | 'exportAllLogs'
    | 'errorEvidenceTimeoutMs'
  >

export type HttpApmExporterOptions = HttpTransportOptions &
  Pick<
    CoreHttpExporterOptions,
    | 'batcherConfig'
    | 'retry'
    | 'successfulTraceSampleRate'
    | 'dangerouslyAllowProdSuccessfulTraceSampling'
    | 'logSuccessfulExports'
    | 'logPrefix'
    | 'exportAllLogs'
    | 'apm'
  >

/**
 * Where batches go. The exporter machinery (batching, error-group deltas,
 * APM alerting, sampling/budgets) is transport-agnostic; this is the seam.
 * HTTP to a telemetry backend is the default implementation; a db-direct
 * transport writes through the same ingest logic without any network.
 */
export interface TelExportTransport {
  /** Destination key shared by this transport's span/log bindings. */
  label: string
  /** Stream every span/log change; APM contributes metrics only. */
  live?: true
  sendSpans(spans: SpanData[]): Promise<ExportResult>
  sendLogs(logs: LogData[]): Promise<ExportResult>
  sendMetrics(metrics: Array<DeltaMetricPoint | GaugeMetricPoint>): Promise<ExportResult>
  sendErrorCreates(errors: ClientErrorCreate[]): Promise<ExportResult>
  sendErrorPatches(patches: ClientErrorGroupChange[]): Promise<ExportResult>
  loadApmBaselines(
    lookbackMs: number,
  ): Promise<ExportResult & { baselines?: ApmEndpointBaseline[] }>
  sendApmLatencyAlert(alert: ApmLatencyRegressionAlert): Promise<ExportResult>
  /** Optional: awaited by exporter `flush()`/`shutdown()`. */
  flush?: () => Promise<void>
}

export type TelExportTransportFactory = (
  deploy: TelDeployResource,
  metrics: TelMetricResource,
) => TelExportTransport

export interface TransportExporterOptions extends CoreHttpExporterOptions {
  transport: TelExportTransportFactory
}

export interface ApmAlertingOptions {
  enabled?: boolean
  minRequests?: number
  cooldownMs?: number
  baselineLookbackMs?: number
  normalizedMetric?: boolean
  slowTraceExport?: boolean | ApmSlowTraceExportOptions
  /**
   * How often to re-fetch baselines from the server while the process is
   * running, so long-lived processes track baseline drift instead of
   * alerting/exemplar-exporting forever against whatever was true at boot.
   * Default 1h. A failed load is retried on its own capped-backoff schedule
   * (not this interval) - see `createApmLatencyAlertingRecorder`.
   */
  baselineRefreshMs?: number
}

export interface ApmSlowTraceExportOptions {
  enabled?: boolean
  degradationRatio?: number
  cooldownMs?: number
  minBaselineSamples?: number
}

export type HttpErrorTrackingExporter = TelExporter
export type HttpApmExporter = TelExporter

const DEFAULT_TIMEOUT_MS = 30_000
const DEFAULT_ERROR_EVIDENCE_TIMEOUT_MS = 30_000
const DEFAULT_RETRY_MAX_ATTEMPTS = 3
const DEFAULT_RETRY_BASE_DELAY_MS = 100
const DEFAULT_RETRY_MAX_DELAY_MS = 5_000
const DEFAULT_APM_SLOW_TRACE_DEGRADATION_RATIO = 1.3
const DEFAULT_APM_SLOW_TRACE_COOLDOWN_MS = 60 * 60 * 1_000
const DEFAULT_APM_SLOW_TRACE_MIN_BASELINE_SAMPLES = 100
const DEFAULT_APM_BASELINE_REFRESH_MS = 60 * 60 * 1_000
const APM_BASELINE_RETRY_BASE_MS = 30_000
const APM_BASELINE_RETRY_CAP_MS = 15 * 60 * 1_000
const DEFAULT_ERROR_LOG_PREFIX = '[tel-http-error-tracking-exporter]'
const DEFAULT_APM_LOG_PREFIX = '[tel-http-apm-exporter]'
const SUCCESSFUL_TRACE_WARNING_INTERVAL_MS = 15 * 60 * 1_000
const DEFAULT_BATCHER_CONFIG: Required<BatcherOptions> = {
  maxBatchSize: 2_048,
  scheduledDelayMs: 60_000,
  maxQueueSize: 16_384,

}

export function createHttpServerErrorTrackingExporter(
  opts: HttpErrorTrackingExporterOptions,
): HttpErrorTrackingExporter {
  return {
    bind: (resource, storage) =>
      bindHttpExporter(
        'error-tracking',
        'server',
        opts,
        createHttpTransport(opts),
        resource.deploy,
        resource.metrics,
        requireStorage(storage),
      ),
  }
}

export function createHttpClientErrorTrackingExporter(
  opts: HttpErrorTrackingExporterOptions,
): HttpErrorTrackingExporter {
  return {
    bind: (resource, storage) =>
      bindHttpExporter(
        'error-tracking',
        'client',
        opts,
        createHttpTransport(opts),
        resource.deploy,
        resource.metrics,
        requireStorage(storage),
      ),
  }
}

/** Error tracking over caller-supplied transport. */
export function createHttpServerApmExporter(
  opts: HttpApmExporterOptions,
): HttpApmExporter {
  return {
    bind: (resource, storage) =>
      bindHttpExporter(
        'apm',
        'server',
        opts,
        createHttpTransport(opts),
        resource.deploy,
        resource.metrics,
        requireStorage(storage),
      ),
  }
}

export function createHttpClientApmExporter(
  opts: HttpApmExporterOptions,
): HttpApmExporter {
  return {
    bind: (resource, storage) =>
      bindHttpExporter(
        'apm',
        'client',
        opts,
        createHttpTransport(opts),
        resource.deploy,
        resource.metrics,
        requireStorage(storage),
      ),
  }
}

export function createServerErrorTrackingExporterWithTransport(
  opts: TransportExporterOptions,
): TelExporter {
  return {
    bind: (resource, storage) =>
      bindHttpExporter(
        'error-tracking',
        'server',
        opts,
        opts.transport,
        resource.deploy,
        resource.metrics,
        requireStorage(storage),
      ),
  }
}

/** Client exporter over a caller-supplied transport. */
export function createClientErrorTrackingExporterWithTransport(
  opts: TransportExporterOptions,
): TelExporter {
  return {
    bind: (resource, storage) =>
      bindHttpExporter(
        'error-tracking',
        'client',
        opts,
        opts.transport,
        resource.deploy,
        resource.metrics,
        requireStorage(storage),
      ),
  }
}

export function createServerApmExporterWithTransport(
  opts: TransportExporterOptions,
): TelExporter {
  return {
    bind: (resource, storage) =>
      bindHttpExporter(
        'apm',
        'server',
        opts,
        opts.transport,
        resource.deploy,
        resource.metrics,
        requireStorage(storage),
      ),
  }
}

export function createClientApmExporterWithTransport(
  opts: TransportExporterOptions,
): TelExporter {
  return {
    bind: (resource, storage) =>
      bindHttpExporter(
        'apm',
        'client',
        opts,
        opts.transport,
        resource.deploy,
        resource.metrics,
        requireStorage(storage),
      ),
  }
}

type HttpExporterMode = 'server' | 'client'
type ExporterConcern = 'error-tracking' | 'apm'

function flattenTrace(trace: TraceData): {
  spans: SpanData[]
  logs: LogData[]
} {
  const spans: SpanData[] = []
  const logs: LogData[] = []
  const visit = (node: TraceData) => {
    const { children, logs: nodeLogs, ...span } = node
    spans.push(span)
    logs.push(...nodeLogs)
    for (const child of children) visit(child)
  }
  visit(trace)
  return { spans, logs }
}

function traceHasError(trace: TraceData): boolean {
  return (
    trace.status === 'error' ||
    trace.error !== undefined ||
    trace.children.some(traceHasError)
  )
}

// Audit logs are stored with `__alwaysExport__: true`; the wire format carries
// `"name env"` so backends can match subscriptions by attribute value.
function _toExportedLog(log: LogData, deploy: TelDeployResource): LogData {
  if (!shouldAlwaysExportLog(log)) return log
  return {
    ...log,
    attributes: {
      ...log.attributes,
      [ALWAYS_EXPORT_LOG_ATTRIBUTE]: formatAlwaysExportValue(deploy),
    },
  }
}

/** Mini transport is native-only. W3C fields remain available to OTEL sinks. */
function _toMiniSpan(span: SpanData): SpanData {
  const {
    baggage: _baggage,
    otel: _otel,
    traceFlags: _traceFlags,
    traceState: _traceState,
    ...native
  } = span
  return native
}

/** Mini transport is native-only. W3C fields remain available to OTEL sinks. */
function _toMiniLog(log: LogData): LogData {
  const {
    baggage: _baggage,
    otel: _otel,
    traceFlags: _traceFlags,
    traceState: _traceState,
    ...native
  } = log
  return native
}

function _toMiniTrace(trace: TraceData): TraceData {
  return {
    ..._toMiniSpan(trace),
    logs: trace.logs.map(_toMiniLog),
    children: trace.children.map(_toMiniTrace),
  }
}

function _toMiniErrorCreate(error: ClientErrorCreate): ClientErrorCreate {
  if (!error.trace) return error
  return { ...error, trace: _toMiniTrace(error.trace) }
}

function bindHttpExporter(
  concern: ExporterConcern,
  mode: HttpExporterMode,
  opts: CoreHttpExporterOptions,
  transportFactory: TelExportTransportFactory,
  deployResource: TelDeployResource,
  metricResource: TelMetricResource,
  storage: TelStorage,
): BoundTelExporter {
  const {
    batcherConfig,
    successfulTraceSampleRate: requestedSuccessfulTraceSampleRate = 0,
    dangerouslyAllowProdSuccessfulTraceSampling = false,
    logSuccessfulExports = false,
    logPrefix = concern === 'error-tracking'
      ? DEFAULT_ERROR_LOG_PREFIX
      : DEFAULT_APM_LOG_PREFIX,
    exportAllLogs = false,
    errorEvidenceTimeoutMs = DEFAULT_ERROR_EVIDENCE_TIMEOUT_MS,
  } = opts

  const resolvedBatcherConfig = {
    ...DEFAULT_BATCHER_CONFIG,
    ...batcherConfig,
  }
  const transport = transportFactory(deployResource, metricResource)
  const lastExportError = new Map<string, string>()
  const successfulTraceGuard = createSuccessfulTraceGuard({
    deployResource,
    requestedSampleRate: requestedSuccessfulTraceSampleRate,
    dangerouslyAllowProdSuccessfulTraceSampling,
    logPrefix,
  })
  const successfulTraceSampleRate = successfulTraceGuard.effectiveSampleRate
  const successSampler = createSampler(successfulTraceSampleRate)
  let errorTracking: ErrorTrackingHandler

  if (logSuccessfulExports) {
    console.info(
      `${logPrefix} exporter ready target=${transport.label} mode=${mode} trace_resource=${formatDeployResource(deployResource)} metric_resource=${formatMetricResource(metricResource)}`,
    )
  }

  const _onExportResult =
    (kind: string) => (result: ExportResult, items: { length: number }) => {
      if (result.code === ExportResultCode.SUCCESS) {
        lastExportError.delete(kind)
        if (logSuccessfulExports) {
          console.info(`${logPrefix} exported ${kind} count=${items.length}`)
        }
      } else {
        const msg = result.error ?? 'unknown'
        if (lastExportError.get(kind) !== msg) {
          lastExportError.set(kind, msg)
          console.error(`${logPrefix} ${kind} export failed: ${msg}`)
        }
      }
    }

  const enqueueLog = storage._createDeliveryBatch<LogData>(
    transport.label,
    async (items) => {
      const result = await exportLogs(items, transport)
      _onExportResult('logs')(result, items)
      return result
    },
    resolvedBatcherConfig,
  )

  const enqueueMetric = storage._createDeliveryBatch<DeltaMetricPoint | GaugeMetricPoint>(
    transport.label,
    async (items) => {
      const result = await exportMetrics(items, transport)
      _onExportResult('metrics')(result, items)
      return result
    },
    { ...resolvedBatcherConfig, maxBatchSize: 25 },
  )

  const errorGroupBatcher = new Batcher<string>(
    {
      async export(items, cb) {
        const groupIds = [...new Set(items)]
        const result = await exportErrorGroupDeltas(groupIds, {
          transport,
          errorTracking,
        })
        if (result.code === ExportResultCode.SUCCESS) {
          errorTracking.ackPending(groupIds)
        }
        cb(result)
      },
    },
    { ...resolvedBatcherConfig, scheduledDelayMs: 0 },
    _onExportResult('error-groups'),
  )
  errorTracking = createErrorTrackingHandler({
    scope: storage._getErrorTrackingConfig().errorGroupScope,
    stackResolver: storage._getErrorTrackingConfig().stackResolver,
    hasRetainedBoundary(data) {
      return storage
        ._getInternalSpans()
        .some(
          (span) =>
            (data.traceId !== undefined &&
              span.id === data.traceId &&
              span.traceId === span.id) ||
            (data.requestId !== undefined &&
              span.id === data.requestId &&
              span.requestId === span.id),
        )
    },
    onGroupsReady(groupIds) {
      for (const groupId of groupIds) errorGroupBatcher.enqueue(groupId)
    },
  })

  const apm =
    concern === 'apm' && mode === 'server' && opts.apm !== false
      ? createApmRequestRecorder({
          enqueueMetric,
          resource: metricResource,
          storage,
          alerting: createApmAlertingConfig(opts.apm, {
            transport,
            logPrefix,
          }),
        })
      : null

  const sink = transport.label
  const live = transport.live === true

  const baseHandlers: BoundTelExporter = {
    metrics: {
      onCreateDelta: enqueueMetric,
      onCreateGauge: enqueueMetric,
    },
    async flush() {
      apm?.flushAggregators()
      await Promise.all([
        storage._flushDelivery(transport.label),
        transport.flush?.() ?? Promise.resolve(),
      ])
    },
    async shutdown() {
      apm?.clearAggregators()
      await Promise.all([
        storage._flushDelivery(transport.label, true),
        transport.flush?.() ?? Promise.resolve(),
      ])
    },
  }

  // Each forced boundary decision lasts until its sampled binding consumes it.
  const forcedBoundaryIds = new Set<string>()
  const onServerBoundaryEnd = (span: SpanData): void => {
    if (live) {
      apm?.onRequestEnd(span)
      return
    }
    if (apm?.onRequestEnd(span).forceTrace) forcedBoundaryIds.add(span.id)
  }
  function consumeForceDecision(boundaryId: string): boolean {
    return forcedBoundaryIds.delete(boundaryId)
  }
  const deliveryRetry = { retries: 0 }
  // (a) metrics/alerts: no sink, receives complete boundaries, runs the
  // server APM recorder. Live mode already streams every log, so the
  // always-export log batcher path is redundant there.
  const apmMetricsHandlers: BoundTelExporter = {
    ...baseHandlers,
    retry: deliveryRetry,
    traces: { onEnd: onCompleteBoundaryEnd },
    requests: { onEnd: onCompleteBoundaryEnd },
    logs: {
      onCreate(log) {
        if (!live && (exportAllLogs || shouldAlwaysExportLog(log))) {
          enqueueLog(_toExportedLog(log, deployResource))
        }
      },
    },
  }
  // (b) sampled successful traces: shares `sink` with error delivery so a
  // span/log sent by one is skipped by the other.
  const apmSampledTraceHandlers: BoundTelExporter = {
    sink,
    retry: deliveryRetry,
    traces: { onEnd: onApmSampledTraceEnd },
    requests: { onEnd: onApmSampledTraceEnd },
  }
  const errorDeliveryHandlers: BoundTelExporter = {
    sink,
    retry: deliveryRetry,
    acceptsBoundary: (trace) =>
      live || traceHasError(trace) || errorTracking.hasBoundaryErrors(trace),
    errors: {
      async onCreate(event) {
        for (const trace of event.traces) await exportTrace(trace, true)
      },
    },
    // Selection sees the complete snapshot; delivery sees only pending
    // revisions. Group readiness and pruned error evidence cannot change
    // the selected boundary while its earlier eager send is in flight.
    traces: {
      onEnd(trace) {
        return exportTrace(trace, true)
      },
    },
    requests: {
      onEnd(trace) {
        return exportTrace(trace, true)
      },
    },
    ...(live
      ? {
          spans: {
            onBatch: sendLiveSpans,
            onCreate: sendLiveSpan,
            onUpdate: sendLiveSpan,
            onEnd: sendLiveSpan,
          },
          logs: {
            onCreate: sendLiveLog,
          },
        }
      : {}),
    async flush() {
      await (transport.flush?.() ?? Promise.resolve())
    },
    async shutdown() {
      await (transport.flush?.() ?? Promise.resolve())
    },
  }
  const errorGroupingHandlers: BoundTelExporter = {
    logs: errorTracking.logs,
    spans: errorTracking.spans,
    traces: errorTracking.traces,
    requests: errorTracking.requests,
    async flush() {
      errorTracking.markTimedOut(Date.now(), errorEvidenceTimeoutMs)
      enqueuePendingErrorGroupIds(errorTracking, errorGroupBatcher)
      await errorGroupBatcher.forceFlush()
    },
    async shutdown() {
      errorTracking.forcePartial('timeout')
      enqueuePendingErrorGroupIds(errorTracking, errorGroupBatcher)
      await errorGroupBatcher.shutdown()
    },
  }
  if (concern === 'apm') {
    return {
      ...apmMetricsHandlers,
      _bindings: live
        ? [apmMetricsHandlers]
        : [apmMetricsHandlers, apmSampledTraceHandlers],
    }
  }
  return {
    ...errorGroupingHandlers,
    _bindings: [
      errorDeliveryHandlers,
      { errors: errorTracking.errors },
      errorGroupingHandlers,
    ],
  }

  function onCompleteBoundaryEnd(trace: TraceData): void {
    if (mode === 'server') onServerBoundaryEnd(trace)
  }

  function onApmSampledTraceEnd(trace: TraceData): Promise<void> | void {
    // Consumed first, before the error early-return: an errored boundary
    // never reaches the sampled-export branch below, so if the decision were
    // consumed there instead a forced-but-errored boundary's entry would
    // never be deleted.
    const forced = consumeForceDecision(trace.id)
    if (traceHasError(trace)) return
    return exportTrace(trace, false, forced)
  }

  function sendLiveSpan(span: SpanData): Promise<void> {
    return sendLiveSpans([span])
  }

  async function sendLiveSpans(spans: SpanData[]): Promise<void> {
    const outgoing = spans.filter(span => !(mode === 'server' && span.origin === 'client'))
    if (outgoing.length === 0) return
    const result = await transport.sendSpans(outgoing.map(span => _toMiniSpan(span)))
    if (result.code === ExportResultCode.FAILED)
      throw new Error(result.error ?? 'live span export failed')
  }

  async function sendLiveLog(log: LogData): Promise<void> {
    if (mode === 'server' && log.origin === 'client') return
    const result = await transport.sendLogs([_toMiniLog(log)])
    if (result.code === ExportResultCode.FAILED) {
      throw new Error(result.error ?? 'live log export failed')
    }
  }

  async function exportTrace(
    trace: TraceData,
    errorDelivery: boolean,
    forced = false,
  ): Promise<void> {
    const flattened = flattenTrace(trace)
    let spans = flattened.spans
    const root = trace
    if (mode === 'server' && root?.origin === 'client') return
    if (!errorDelivery) {
      if (
        !root ||
        (!forced && !successSampler.shouldExport(root.traceId ?? root.id))
      ) {
        successfulTraceGuard.recordSuccessTrace(false)
        return
      }
      spans = spans.map((span) => toMinimalSpan(span))
      if (successfulTraceSampleRate < 1) {
        spans = spans.map((span) =>
          span.id === trace.id
            ? {
                ...span,
                attributes: {
                  ...span.attributes,
                  'tel.client_sample_rate': successfulTraceSampleRate,
                  'tel.server_sample_rate': 1,
                },
              }
            : span,
        )
      }
      successfulTraceGuard.recordSuccessTrace(true)
    }
    const logs = errorDelivery ? flattened.logs : []
    const results = await Promise.all([
      spans.length > 0
        ? transport.sendSpans(spans.map((span) => _toMiniSpan(span)))
        : Promise.resolve({ code: ExportResultCode.SUCCESS }),
      logs.length > 0
        ? transport.sendLogs(logs.map((log) => _toMiniLog(log)))
        : Promise.resolve({ code: ExportResultCode.SUCCESS }),
    ])
    const failure = results.find(
      (result) => result.code === ExportResultCode.FAILED,
    )
    if (failure) {
      const message =
        'error' in failure && typeof failure.error === 'string'
          ? failure.error
          : 'trace export failed'
      console.error(`${logPrefix} traces export failed: ${message}`)
      throw new Error(message)
    }
  }
}

type ApmRequestRecorder = {
  onRequestEnd(span: SpanData): ApmRequestDecision
  flushAggregators(): void
  clearAggregators(): void
}
type ApmRequestDecision = { forceTrace: boolean }

type ApmAlertingRuntimeConfig = {
  enabled: true
  minRequests: number
  cooldownMs: number
  baselineLookbackMs: number
  baselineRefreshMs: number
  normalizedMetric: boolean
  transport: TelExportTransport
  slowTraceExport: ApmSlowTraceExportRuntimeConfig | null
  logPrefix: string
}

type ApmSlowTraceExportRuntimeConfig = {
  degradationRatio: number
  cooldownMs: number
  minBaselineSamples: number
}

export type ApmEndpointBaseline = {
  endpoint: string
  latency: { p50: number; p90: number; p95: number; p99: number }
  normalizedLatency?: { p50: number; p90: number; p95: number; p99: number }
  requestsPerMinuteP50: number
  requestsPerMinuteP95: number
  sampleCount: number
  updatedAt: number
}

type ApmWindowSample = {
  at: number
  duration: number
  normalizedDuration: number
  inputSize: number
  outputSize: number
  failed: boolean
}

export type ApmEndpointWindow = {
  endpoint: string
  count: number
  durationP50: number
  durationP95: number
  normalizedDurationP50?: number
  normalizedDurationP95?: number
  inputSizeP50: number
  inputSizeP95: number
  outputSizeP50: number
  outputSizeP95: number
  previousInputSizeP50: number
  previousOutputSizeP50: number
  errors: number
}

export type ApmLatencyRegressionAlert = {
  endpoint: string
  detectedAt: number
  windowMs: number
  current: ApmEndpointWindow
  baseline: ApmEndpointBaseline
  reason: 'latency_regression_same_payload'
}

function createApmRequestRecorder(opts: {
  enqueueMetric: (metric: DeltaMetricPoint | GaugeMetricPoint) => void
  resource: TelMetricResource
  storage: TelStorage
  alerting: ApmAlertingRuntimeConfig | null
}): ApmRequestRecorder {
  const apmStorage = createExporterTelStorage({
    config: { metricsMode: opts.storage.metrics.mode() },
    handlers: [
      {
        metrics: {
          onCreateDelta(metric) {
            opts.enqueueMetric(metric)
          },
        },
      },
    ],
  })
  const apmMetrics = _createMetricsFactory(() => apmStorage, opts.resource, { mode: opts.storage.metrics.mode(), metricSeriesLimit: 1_000 })
  const requestCounters = new Map<string, ApmCounter>()
  const durationHistograms = new Map<string, ApmHistogram>()
  const normalizedDurationHistograms = new Map<string, ApmHistogram>()
  const alerting = opts.alerting
    ? createApmLatencyAlertingRecorder(opts.alerting)
    : null

  return {
    onRequestEnd(span) {
      if (span.status !== 'ok' && span.status !== 'error') {
        return { forceTrace: false }
      }
      const endpoint = span.name
      const successful = span.status === 'ok'
      const failed = span.status === 'error'
      const requestCounter = getApmRequestCounter(
        apmMetrics,
        requestCounters,
        endpoint,
        failed,
      )
      requestCounter.add(1)
      const forceTrace = alerting?.onRequestEnd(span).forceTrace === true
      if (!successful) return { forceTrace }
      getApmDurationHistogram(apmMetrics, durationHistograms, endpoint)._recordWithExemplar(
        span.duration ?? 0,
        undefined,
        {
          traceId: span.traceId ?? span.id,
          spanId: span.id,
          requestId: span.requestId,
          ...(span.otel ? { otel: { traceId: span.otel.traceId } } : {}),
        },
      )
      if (opts.alerting?.normalizedMetric) {
        getApmNormalizedDurationHistogram(
          apmMetrics,
          normalizedDurationHistograms,
          endpoint,
        ).record(normalizedDuration(span))
      }
      return { forceTrace }
    },
    flushAggregators() {
      apmMetrics.flushAggregators()
    },
    clearAggregators() {
      apmMetrics.clearAggregators()
      requestCounters.clear()
      durationHistograms.clear()
      normalizedDurationHistograms.clear()
      alerting?.clear()
    },
  }
}

function createApmAlertingConfig(
  apm: CoreHttpExporterOptions['apm'],
  ctx: {
    transport: TelExportTransport
    logPrefix: string
  },
): ApmAlertingRuntimeConfig | null {
  if (apm === false) return null
  const alerting = typeof apm === 'object' ? apm.alerting : undefined
  if (alerting === false) return null
  const opts = typeof alerting === 'object' ? alerting : {}
  if (opts.enabled === false) return null
  const slowTraceExport = createApmSlowTraceExportConfig(opts.slowTraceExport)
  return {
    enabled: true,
    minRequests: opts.minRequests ?? 20,
    cooldownMs: opts.cooldownMs ?? 60 * 60 * 1_000,
    baselineLookbackMs: opts.baselineLookbackMs ?? 7 * 24 * 60 * 60 * 1_000,
    baselineRefreshMs:
      opts.baselineRefreshMs ?? DEFAULT_APM_BASELINE_REFRESH_MS,
    normalizedMetric: opts.normalizedMetric ?? false,
    transport: ctx.transport,
    slowTraceExport,
    logPrefix: ctx.logPrefix,
  }
}

function createApmLatencyAlertingRecorder(config: ApmAlertingRuntimeConfig) {
  const windowMs = 60_000
  const samplesByEndpoint = new Map<string, ApmWindowSample[]>()
  const lastAlertAt = new Map<string, number>()
  const lastSlowTraceByEndpoint = new Map<
    string,
    { exportedAt: number; duration: number }
  >()
  let baselines = new Map<string, ApmEndpointBaseline>()
  // Loud-failure state: `consecutiveFailures` gates the warn to once per
  // failure *streak* (not once per retry, which would spam logs every
  // 30s-15min forever); it resets to 0 on the next successful load, which
  // re-arms the warning for the next streak.
  let consecutiveFailures = 0
  let refreshTimer: ReturnType<typeof setTimeout> | null = null
  let retryTimer: ReturnType<typeof setTimeout> | null = null
  let stopped = false

  function warnDegraded(error: string): void {
    if (consecutiveFailures !== 1) return
    console.warn(
      `${config.logPrefix} APM baseline load failed, retrying with backoff: ${error}. ` +
        'Latency-regression alerting and slow-trace exemplar export are ' +
        'degraded (fail silent-closed) until baselines load.',
    )
  }

  // Baselines silently going stale for a long-lived process is exactly the
  // "watchdog that looks alive but never barks" failure mode this task
  // exists to close - baseline loads should retry forever, not attempt once
  // and give up (see task's defect #1: boot-time transient unreachability
  // used to permanently disable alerting + slow-trace export for the
  // process's whole lifetime).
  async function loadBaselines(): Promise<void> {
    const result = await config.transport.loadApmBaselines(
      config.baselineLookbackMs,
    )
    if (stopped) return
    if (result.code !== ExportResultCode.SUCCESS) {
      consecutiveFailures++
      warnDegraded(result.error ?? 'unknown error')
      scheduleRetry()
      return
    }
    consecutiveFailures = 0
    baselines = new Map(
      (result.baselines ?? []).map((baseline) => [baseline.endpoint, baseline]),
    )
    scheduleRefresh()
  }

  function scheduleRetry(): void {
    if (stopped) return
    const backoff = APM_BASELINE_RETRY_BASE_MS * 2 ** (consecutiveFailures - 1)
    const delayMs = Math.min(backoff, APM_BASELINE_RETRY_CAP_MS)
    retryTimer = setTimeout(() => void loadBaselines(), delayMs)
  }

  function scheduleRefresh(): void {
    if (stopped) return
    refreshTimer = setTimeout(
      () => void loadBaselines(),
      config.baselineRefreshMs,
    )
  }

  void loadBaselines()

  return {
    onRequestEnd(span: SpanData) {
      if (span.status !== 'ok' && span.status !== 'error') {
        return { forceTrace: false }
      }
      const now = Date.now()
      const endpoint = span.name
      const samples = samplesByEndpoint.get(endpoint) ?? []
      samples.push({
        at: now,
        duration: span.duration ?? 0,
        normalizedDuration: normalizedDuration(span),
        inputSize: span.inputSize ?? 0,
        outputSize: span.outputSize ?? 0,
        failed: span.status === 'error',
      })
      const kept = samples.filter((sample) => sample.at >= now - windowMs * 2)
      samplesByEndpoint.set(endpoint, kept)
      const baseline = baselines.get(endpoint)
      const forceTrace = shouldExportSlowTrace({
        span,
        now,
        baseline,
        config: config.slowTraceExport,
        lastSlowTraceByEndpoint,
      })
      // Second half of the traffic floor (see decision comment in
      // `maybeCreateLatencyRegressionAlert`): a baseline needs >=100 samples
      // before it is trusted enough to alert against. Not currently exposed
      // via `ApmAlertingOptions` - raise here if a project needs a different
      // bar.
      if (!baseline || baseline.sampleCount < 100) return { forceTrace }
      const alert = maybeCreateLatencyRegressionAlert({
        endpoint,
        now,
        windowMs,
        samples: kept,
        baseline,
        minRequests: config.minRequests,
      })
      if (!alert) return { forceTrace }
      const cooldownKey = `${endpoint}:${alert.reason}`
      const previousAlertAt = lastAlertAt.get(cooldownKey) ?? 0
      if (previousAlertAt > now - config.cooldownMs) return { forceTrace }
      lastAlertAt.set(cooldownKey, now)
      void config.transport.sendApmLatencyAlert(alert)
      return { forceTrace }
    },
    clear() {
      samplesByEndpoint.clear()
      lastAlertAt.clear()
      lastSlowTraceByEndpoint.clear()
      stopped = true
      if (refreshTimer !== null) clearTimeout(refreshTimer)
      if (retryTimer !== null) clearTimeout(retryTimer)
      refreshTimer = null
      retryTimer = null
    },
  }
}

function createApmSlowTraceExportConfig(
  opts: ApmAlertingOptions['slowTraceExport'],
): ApmSlowTraceExportRuntimeConfig | null {
  if (opts === false) return null
  const config = typeof opts === 'object' ? opts : {}
  if (config.enabled === false) return null
  return {
    degradationRatio:
      config.degradationRatio ?? DEFAULT_APM_SLOW_TRACE_DEGRADATION_RATIO,
    cooldownMs: config.cooldownMs ?? DEFAULT_APM_SLOW_TRACE_COOLDOWN_MS,
    minBaselineSamples:
      config.minBaselineSamples ?? DEFAULT_APM_SLOW_TRACE_MIN_BASELINE_SAMPLES,
  }
}

function shouldExportSlowTrace(input: {
  span: SpanData
  now: number
  baseline: ApmEndpointBaseline | undefined
  config: ApmSlowTraceExportRuntimeConfig | null
  lastSlowTraceByEndpoint: Map<string, { exportedAt: number; duration: number }>
}): boolean {
  if (!input.config) return false
  if (input.span.status !== 'ok') return false
  if (!input.baseline) {
    return false
  }
  if (input.baseline.sampleCount < input.config.minBaselineSamples) return false
  const duration = input.span.duration ?? 0
  const slowThreshold =
    input.baseline.latency.p95 * input.config.degradationRatio
  if (duration < slowThreshold) return false
  const endpoint = input.span.name
  const last = input.lastSlowTraceByEndpoint.get(endpoint)
  if (
    last &&
    last.exportedAt > input.now - input.config.cooldownMs &&
    duration < last.duration * input.config.degradationRatio
  ) {
    return false
  }
  input.lastSlowTraceByEndpoint.set(endpoint, {
    exportedAt: input.now,
    duration,
  })
  return true
}

function maybeCreateLatencyRegressionAlert(input: {
  endpoint: string
  now: number
  windowMs: number
  samples: ApmWindowSample[]
  baseline: ApmEndpointBaseline
  minRequests: number
}): ApmLatencyRegressionAlert | null {
  const current = input.samples.filter(
    (sample) => sample.at >= input.now - input.windowMs,
  )
  const previous = input.samples.filter(
    (sample) =>
      sample.at < input.now - input.windowMs &&
      sample.at >= input.now - input.windowMs * 2,
  )
  // Traffic floor, by design: an endpoint only becomes alert-eligible once it
  // sees >=max(20, minRequests) req/min in *two consecutive* 1-min windows,
  // on top of the >=100-sample baseline gate in the caller. Below that, a
  // single slow/failed request can swing p50/p95 enough to look like a 3x/2x
  // regression - low-traffic services never alert, on purpose, rather than
  // firing on noise. 20 is a hard floor, not just a default: it is NOT
  // relaxable via `ApmAlertingOptions.minRequests` (that option can only
  // raise the bar, e.g. for endpoints where even 20 req/min is too thin to
  // trust). Per-project tuning is `minRequests` (floor) and the baseline
  // `sampleCount` gate (see caller); the regression multipliers below
  // (3x p50 / 2x p95) are not currently configurable.
  if (current.length < Math.max(20, input.minRequests)) return null
  if (previous.length < Math.max(20, input.minRequests)) return null
  const window = apmEndpointWindow(input.endpoint, current, previous)
  if (window.outputSizeP50 > window.previousOutputSizeP50 * 1.5 + 1024) {
    return null
  }
  if (window.inputSizeP50 > window.previousInputSizeP50 * 1.5 + 1024) {
    return null
  }
  if (window.durationP50 < input.baseline.latency.p50 * 3) return null
  if (window.durationP95 < input.baseline.latency.p95 * 2) return null
  if (window.errors / window.count > 0.2) return null
  if (
    input.baseline.normalizedLatency &&
    (window.normalizedDurationP50 ?? 0) <
      input.baseline.normalizedLatency.p50 * 2
  ) {
    return null
  }
  return {
    endpoint: input.endpoint,
    detectedAt: input.now,
    windowMs: input.windowMs,
    current: window,
    baseline: input.baseline,
    reason: 'latency_regression_same_payload',
  }
}

function apmEndpointWindow(
  endpoint: string,
  current: ApmWindowSample[],
  previous: ApmWindowSample[],
): ApmEndpointWindow {
  return {
    endpoint,
    count: current.length,
    durationP50: percentile(
      current.map((sample) => sample.duration),
      0.5,
    ),
    durationP95: percentile(
      current.map((sample) => sample.duration),
      0.95,
    ),
    normalizedDurationP50: percentile(
      current.map((sample) => sample.normalizedDuration),
      0.5,
    ),
    normalizedDurationP95: percentile(
      current.map((sample) => sample.normalizedDuration),
      0.95,
    ),
    inputSizeP50: percentile(
      current.map((sample) => sample.inputSize),
      0.5,
    ),
    inputSizeP95: percentile(
      current.map((sample) => sample.inputSize),
      0.95,
    ),
    outputSizeP50: percentile(
      current.map((sample) => sample.outputSize),
      0.5,
    ),
    outputSizeP95: percentile(
      current.map((sample) => sample.outputSize),
      0.95,
    ),
    previousInputSizeP50: percentile(
      previous.map((sample) => sample.inputSize),
      0.5,
    ),
    previousOutputSizeP50: percentile(
      previous.map((sample) => sample.outputSize),
      0.5,
    ),
    errors: current.filter((sample) => sample.failed).length,
  }
}

function normalizedDuration(span: SpanData): number {
  const payloadKiB = Math.max(
    1,
    ((span.inputSize ?? 0) + (span.outputSize ?? 0)) / 1024,
  )
  return (span.duration ?? 0) / payloadKiB
}

function percentile(values: number[], quantile: number): number {
  if (values.length === 0) return 0
  const sorted = values.toSorted((left, right) => left - right)
  const index = Math.min(
    sorted.length - 1,
    Math.max(0, Math.ceil(sorted.length * quantile) - 1),
  )
  return sorted[index] ?? 0
}

type PendingClientErrorCreate = ClientErrorCreate & {
  ready: boolean
  createdAt: number
}

function createErrorTrackingHandler(opts: {
  scope?: string
  stackResolver?: ClientStackResolver
  hasRetainedBoundary(data: TelErrorData): boolean
  onGroupsReady(groupIds: string[]): void
}) {
  const groups = new Map<string, ClientErrorGroup>()
  const actors = new Map<
    string,
    { userIds: Set<string>; companyIds: Set<string> }
  >()
  const pendingCreates = new Map<string, PendingClientErrorCreate>()
  const pendingChanges = new Map<string, ClientErrorGroupChange>()
  const seenRefs = new Set<string>()
  const seenOccurrences = new Set<string>()

  function recordError(input: {
    ref: string
    data: NonNullable<SpanData['error']>
    userId?: string
    companyId?: string
    target: { fingerprint?: string }
    timestamp: number
  }) {
    if (input.data.level === 'warning') return
    if (seenRefs.has(input.ref)) return
    seenRefs.add(input.ref)

    const seed = createClientErrorGroupSeed({
      data: input.data,
      originalError: input.data.error,
      userInfo: {
        ...(input.userId && { userId: input.userId }),
        ...(input.companyId && { companyId: input.companyId }),
      },
      timestamp: input.timestamp,
      stackResolver: opts.stackResolver,
    })
    const groupId = scopeErrorGroupId(seed.group.id, opts.scope)
    const occurrence: ClientErrorOccurrence = {
      ...seed.occurrence,
      groupId,
    }
    const groupSeed = { ...seed.group, id: groupId }
    input.target.fingerprint = groupId
    const occurrenceKey = errorOccurrenceKey(occurrence)
    if (seenOccurrences.has(occurrenceKey)) {
      // The same error reaches us through both the log record (no user info)
      // and the error event (with user info) — keep the first occurrence but
      // still merge the late-arriving actor attribution.
      recordLateActors(groupId, occurrence)
      return
    }
    seenOccurrences.add(occurrenceKey)

    const actorState = getActors(actors, groupId)
    const userIds = occurrence.userId
      ? addActor(actorState.userIds, occurrence.userId)
      : []
    const companyIds = occurrence.companyId
      ? addActor(actorState.companyIds, occurrence.companyId)
      : []
    const change: ClientErrorGroupChange = {
      groupId,
      count: 1,
      userIds,
      companyIds,
      occurrence,
    }
    const mergedChange = mergeClientErrorGroupChange(
      pendingChanges.get(groupId),
      change,
    )
    pendingChanges.set(groupId, mergedChange)
    // An unsent create holds its change by reference — keep it current so
    // occurrences recorded before the flush aren't dropped from the create.
    const pendingCreate = pendingCreates.get(groupId)
    if (pendingCreate) pendingCreate.change = mergedChange

    const existing = groups.get(groupId)
    if (existing) {
      existing.status = 'ongoing'
      existing.occurrences++
      existing.affectedUsers = actorState.userIds.size
      existing.affectedCompanies = actorState.companyIds.size
      existing.lastSeen = occurrence.timestamp
      existing.lastTraceId = occurrence.traceId
      existing.lastSpanId = occurrence.spanId
      existing.lastLogId = occurrence.logId
      existing.lastRequestId = occurrence.requestId
      existing.error = occurrence.error
      return
    }

    const group: ClientErrorGroup = {
      ...groupSeed,
      status: 'new',
      occurrences: 1,
      affectedUsers: actorState.userIds.size,
      affectedCompanies: actorState.companyIds.size,
      firstSeen: occurrence.timestamp,
      firstTraceId: occurrence.traceId,
      firstSpanId: occurrence.spanId,
      firstLogId: occurrence.logId,
      firstRequestId: occurrence.requestId,
      lastSeen: occurrence.timestamp,
      lastTraceId: occurrence.traceId,
      lastSpanId: occurrence.spanId,
      lastLogId: occurrence.logId,
      lastRequestId: occurrence.requestId,
    }
    groups.set(groupId, group)
    const hasActiveUnit = opts.hasRetainedBoundary(input.data)
    pendingCreates.set(groupId, {
      group,
      change: pendingChanges.get(groupId) ?? change,
      evidence: hasActiveUnit
        ? { status: 'partial', reason: 'timeout' }
        : { status: 'partial', reason: 'no-active-unit' },
      ready: !hasActiveUnit,
      createdAt: occurrence.timestamp,
    })
    if (!hasActiveUnit) opts.onGroupsReady([groupId])
  }

  function recordLateActors(
    groupId: string,
    occurrence: ClientErrorOccurrence,
  ): void {
    if (!occurrence.userId && !occurrence.companyId) return
    const actorState = getActors(actors, groupId)
    const userIds = occurrence.userId
      ? addActor(actorState.userIds, occurrence.userId)
      : []
    const companyIds = occurrence.companyId
      ? addActor(actorState.companyIds, occurrence.companyId)
      : []
    if (userIds.length === 0 && companyIds.length === 0) return
    const group = groups.get(groupId)
    if (!group) return
    const merged = mergeClientErrorGroupChange(pendingChanges.get(groupId), {
      groupId,
      count: 0,
      userIds,
      companyIds,
      occurrence,
    })
    pendingChanges.set(groupId, merged)
    group.affectedUsers = actorState.userIds.size
    group.affectedCompanies = actorState.companyIds.size
    const pendingCreate = pendingCreates.get(groupId)
    if (pendingCreate) pendingCreate.change = merged
    else opts.onGroupsReady([groupId])
  }

  function onBoundaryEnd(trace: TraceData): void {
    const traceGroupIds = findErrorGroupIds(trace)
    const evictedGroupIds = findPendingBoundaryGroupIds(
      pendingCreates,
      trace,
    ).filter((groupId) => !traceGroupIds.includes(groupId))
    const groupIds = [...new Set([...traceGroupIds, ...evictedGroupIds])]
    if (groupIds.length === 0) return

    const evidence = pruneErrorBranches(trace)
    for (const groupId of traceGroupIds) {
      const create = pendingCreates.get(groupId)
      if (create) {
        pendingCreates.set(groupId, {
          ...create,
          evidence: { status: 'full' },
          trace: evidence,
          ready: true,
        })
      }
    }
    for (const groupId of evictedGroupIds) {
      const create = pendingCreates.get(groupId)
      if (create) {
        pendingCreates.set(groupId, {
          ...create,
          evidence: { status: 'partial', reason: 'evicted' },
          ready: true,
        })
      }
    }
    opts.onGroupsReady(groupIds)
  }

  return {
    errors: {
      onCreate(event: TelErrorEvent) {
        if (opts.hasRetainedBoundary(event.data)) return
        recordError({
          ref: `error:${event.data.logId ?? event.data.spanId ?? String(Date.now())}`,
          data: event.data,
          userId: event.userInfo.userId,
          companyId: event.userInfo.companyId,
          target: {},
          timestamp: Date.now(),
        })
      },
    },
    logs: {
      onCreate(log: LogData) {
        if (!log.error) return
        recordError({
          ref: `log:${log.id}`,
          data: log.error,
          userId: log.userId,
          companyId: log.companyId,
          target: log,
          timestamp: Date.now(),
        })
      },
    },
    spans: {
      onEnd(span: SpanData) {
        if (!span.error) return
        recordError({
          ref: `span:${span.id}`,
          data: span.error,
          userId: span.userId,
          companyId: span.companyId,
          target: span,
          timestamp: span.timestamp ?? Date.now(),
        })
      },
    },
    traces: {
      onEnd(trace: TraceData) {
        onBoundaryEnd(trace)
      },
    },
    requests: {
      onEnd(trace: TraceData) {
        onBoundaryEnd(trace)
      },
    },
    markTimedOut(now: number, timeoutMs: number) {
      for (const [groupId, create] of pendingCreates) {
        if (create.ready) continue
        if (now - create.createdAt < timeoutMs) continue
        pendingCreates.set(groupId, {
          ...create,
          evidence: { status: 'partial', reason: 'timeout' },
          ready: true,
        })
      }
    },
    forcePartial(reason: ClientErrorEvidenceReason) {
      for (const [groupId, create] of pendingCreates) {
        if (create.ready) continue
        pendingCreates.set(groupId, {
          ...create,
          evidence: { status: 'partial', reason },
          ready: true,
        })
      }
    },
    listPendingCreates(groupIds: string[]) {
      const ids = new Set(groupIds)
      return Array.from(pendingCreates.entries())
        .filter(([groupId, create]) => ids.has(groupId) && create.ready)
        .map(([, create]) => {
          const result: ClientErrorCreate = {
            group: cloneErrorGroup(create.group),
            change: cloneErrorGroupChange(create.change),
            evidence: create.evidence,
          }
          if (create.trace) result.trace = create.trace
          return result
        })
    },
    listPendingPatches(groupIds: string[]) {
      const ids = new Set(groupIds)
      return Array.from(pendingChanges.entries())
        .filter(([groupId]) => ids.has(groupId) && !pendingCreates.has(groupId))
        .map(([, change]) => cloneErrorGroupChange(change))
    },
    listPendingGroupIds() {
      const readyCreates = Array.from(pendingCreates.entries())
        .filter(([, create]) => create.ready)
        .map(([groupId]) => groupId)
      const patchIds = Array.from(pendingChanges.keys()).filter(
        (groupId) => !pendingCreates.has(groupId),
      )
      return [...new Set([...readyCreates, ...patchIds])]
    },
    ackPending(groupIds: string[]) {
      for (const groupId of groupIds) {
        pendingCreates.delete(groupId)
        pendingChanges.delete(groupId)
      }
    },
    isNewGroup(groupId: string) {
      return pendingCreates.has(groupId)
    },
    hasBoundaryErrors(trace: TraceData) {
      return findPendingBoundaryGroupIds(pendingCreates, trace).length > 0
    },
  }
}

type ErrorTrackingHandler = ReturnType<typeof createErrorTrackingHandler>

function exportLogs(
  logs: LogData[],
  transport: TelExportTransport,
): Promise<ExportResult> {
  if (logs.length === 0) {
    return Promise.resolve({ code: ExportResultCode.SUCCESS })
  }

  return transport.sendLogs(logs.map((log) => _toMiniLog(log)))
}

function exportMetrics(
  metrics: Array<DeltaMetricPoint | GaugeMetricPoint>,
  transport: TelExportTransport,
): Promise<ExportResult> {
  if (metrics.length === 0) {
    return Promise.resolve({ code: ExportResultCode.SUCCESS })
  }

  return transport.sendMetrics(metrics)
}

async function exportErrorGroupDeltas(
  groupIds: string[],
  opts: {
    transport: TelExportTransport
    errorTracking: ErrorTrackingHandler
  },
): Promise<ExportResult> {
  const errors = opts.errorTracking.listPendingCreates(groupIds)
  const patches = opts.errorTracking.listPendingPatches(groupIds)
  if (errors.length === 0 && patches.length === 0) {
    return { code: ExportResultCode.SUCCESS }
  }
  if (errors.length > 0) {
    const createResult = await opts.transport.sendErrorCreates(
      errors.map((error) => _toMiniErrorCreate(error)),
    )
    if (createResult.code === ExportResultCode.FAILED) return createResult
  }

  if (patches.length === 0) return { code: ExportResultCode.SUCCESS }
  return opts.transport.sendErrorPatches(patches)
}

/**
 * Public HTTP transport factory: mini-telemetry wire protocol. Used by
 * `createHttp*Exporter`; apps can wrap it (e.g. remote-trace marking) and
 * pass the wrapped factory to the `*WithTransport` exporters.
 */
export function createHttpTransport(
  opts: HttpTransportOptions,
): TelExportTransportFactory {
  const {
    url,
    headers = {},
    timeoutMs = DEFAULT_TIMEOUT_MS,
    retry,
    fetch: fetchFn = fetch,
  } = opts
  const retryConfig = {
    maxAttempts: retry?.maxAttempts ?? DEFAULT_RETRY_MAX_ATTEMPTS,
    baseDelayMs: retry?.baseDelayMs ?? DEFAULT_RETRY_BASE_DELAY_MS,
    maxDelayMs: retry?.maxDelayMs ?? DEFAULT_RETRY_MAX_DELAY_MS,
  }
  const baseUrl = normalizeMiniTelemetryBaseUrl(url)
  const requestHeaders = { ...headers }
  const post = (
    target: string,
    body: Parameters<typeof _postJson>[1],
  ): Promise<ExportResult> =>
    _postJson(target, body, requestHeaders, timeoutMs, retryConfig, fetchFn)

  return (deploy, metrics) => ({
    label: baseUrl,
    sendSpans: (spans) =>
      post(_deployUrl(baseUrl, deploy, '/traces'), { spans }),
    sendLogs: (logs) => post(_deployUrl(baseUrl, deploy, '/logs'), { logs }),
    sendMetrics: (slimmed) =>
      post(_metricUrl(baseUrl, metrics, '/metrics'), { metrics: slimmed }),
    sendErrorCreates: (errors) =>
      post(_deployUrl(baseUrl, deploy, '/error-groups'), { errors }),
    sendErrorPatches: (patches) =>
      post(_deployUrl(baseUrl, deploy, '/error-groups/patches'), { patches }),
    async loadApmBaselines(lookbackMs) {
      const target = new URL(_metricUrl(baseUrl, metrics, '/apm/baselines'))
      target.searchParams.set('lookbackMs', String(lookbackMs))
      const result = await _getJson(
        target.toString(),
        requestHeaders,
        timeoutMs,
        retryConfig,
        fetchFn,
      )
      if (result.code !== ExportResultCode.SUCCESS) return result
      const data = result.data as { baselines?: ApmEndpointBaseline[] }
      return { code: result.code, baselines: data.baselines ?? [] }
    },
    sendApmLatencyAlert: (alert) =>
      post(
        _metricUrl(baseUrl, metrics, '/apm/alerts/latency-regressions'),
        alert,
      ),
  })
}

async function _postJson(
  url: string,
  body:
    | { spans: SpanData[] }
    | { logs: LogData[] }
    | { metrics: unknown[] }
    | { errors: ClientErrorCreate[] }
    | { patches: ClientErrorGroupChange[] }
    | ApmLatencyRegressionAlert,
  headers: Record<string, string>,
  timeoutMs: number,
  retry: Required<ExporterRetryOptions>,
  fetchFn: typeof fetch,
): Promise<ExportResult> {
  const maxAttempts = Math.max(1, retry.maxAttempts)
  let lastError = ''

  for (let attempt = 1; attempt <= maxAttempts; attempt++) {
    const controller = new AbortController()
    const timeoutId = setTimeout(() => {
      controller.abort()
    }, timeoutMs)

    try {
      const response = await fetchFn(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...headers,
        },
        // Span inputs/outputs may hold bigint (driver revisions). JSON has no bigint, so export its decimal
        // string, as OTLP/JSON does for 64-bit integers.
        body: JSON.stringify(body, (_key, value: unknown) => typeof value === 'bigint' ? value.toString() : value),
        signal: controller.signal,
      })

      if (response.ok) {
        return { code: ExportResultCode.SUCCESS }
      }

      const text = await response.text().catch(() => '')
      lastError = formatHttpError(response, text)

      if (!isRetryableStatus(response.status) || attempt === maxAttempts) {
        return {
          code: ExportResultCode.FAILED,
          error: lastError,
        }
      }

      await delay(getRetryDelayMs(response, retry, attempt))
    } catch (error) {
      const message = error instanceof Error ? error.message : String(error)
      lastError = message

      if (attempt === maxAttempts) {
        return {
          code: ExportResultCode.FAILED,
          error: message,
        }
      }

      await delay(getRetryDelayMs(undefined, retry, attempt))
    } finally {
      clearTimeout(timeoutId)
    }
  }

  return {
    code: ExportResultCode.FAILED,
    error: lastError || 'Export failed',
  }
}

async function _getJson(
  url: string,
  headers: Record<string, string>,
  timeoutMs: number,
  retry: Required<ExporterRetryOptions>,
  fetchFn: typeof fetch,
): Promise<ExportResult & { data?: unknown }> {
  const maxAttempts = Math.max(1, retry.maxAttempts)
  let lastError = ''

  for (let attempt = 1; attempt <= maxAttempts; attempt++) {
    const controller = new AbortController()
    const timeoutId = setTimeout(() => {
      controller.abort()
    }, timeoutMs)

    try {
      const response = await fetchFn(url, {
        method: 'GET',
        headers,
        signal: controller.signal,
      })

      if (response.ok) {
        const data = (await response.json()) as unknown
        return { code: ExportResultCode.SUCCESS, data }
      }

      const text = await response.text().catch(() => '')
      lastError = formatHttpError(response, text)

      if (!isRetryableStatus(response.status) || attempt === maxAttempts) {
        return {
          code: ExportResultCode.FAILED,
          error: lastError,
        }
      }

      await delay(getRetryDelayMs(response, retry, attempt))
    } catch (error) {
      const message = error instanceof Error ? error.message : String(error)
      lastError = message

      if (attempt === maxAttempts) {
        return {
          code: ExportResultCode.FAILED,
          error: message,
        }
      }

      await delay(getRetryDelayMs(undefined, retry, attempt))
    } finally {
      clearTimeout(timeoutId)
    }
  }

  return {
    code: ExportResultCode.FAILED,
    error: lastError || 'Export failed',
  }
}

function formatHttpError(response: Response, text: string): string {
  const detail = formatHttpErrorBody(text)
  return `HTTP ${response.status}: ${response.statusText}${detail ? ` - ${detail}` : ''}`
}

function isRetryableStatus(status: number): boolean {
  return status === 429 || status === 502 || status === 503 || status === 504
}

function getRetryDelayMs(
  response: Response | undefined,
  retry: Required<ExporterRetryOptions>,
  attempt: number,
): number {
  const retryAfter = response ? parseRetryAfter(response.headers) : undefined
  const backoff =
    retry.baseDelayMs * 2 ** (attempt - 1) * (Math.random() + 1)
  return Math.min(retryAfter ?? backoff, retry.maxDelayMs)
}

function parseRetryAfter(headers: Headers): number | undefined {
  const value = headers.get('Retry-After')
  if (!value) return undefined

  const seconds = Number(value)
  if (Number.isFinite(seconds) && seconds >= 0) {
    return seconds * 1000
  }

  const date = Date.parse(value)
  if (Number.isNaN(date)) return undefined
  return Math.max(0, date - Date.now())
}

function delay(ms: number): Promise<void> {
  return new Promise((resolve) => {
    setTimeout(resolve, ms)
  })
}

function formatHttpErrorBody(text: string): string {
  if (!text) return ''

  try {
    const parsed: unknown = JSON.parse(text)
    if (!isRecord(parsed)) return text
    const error = stringField(parsed, 'error')
    const cause = stringField(parsed, 'cause')
    const stack = stringField(parsed, 'stack')
    if (!(error || cause || stack)) return text
    return [
      error ? `server error: ${error}` : undefined,
      cause ? `cause: ${cause}` : undefined,
      stack,
    ]
      .filter((part) => part !== undefined)
      .join('\n')
  } catch {
    return text
  }
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null
}

function stringField(
  record: Record<string, unknown>,
  key: string,
): string | undefined {
  const value = record[key]
  return typeof value === 'string' ? value : undefined
}

function _deployUrl(
  baseUrl: string,
  resource: TelDeployResource,
  suffix: string,
): string {
  return `${baseUrl}/${encodeURIComponent(resource.name)}/${encodeURIComponent(resource.env)}/${encodeURIComponent(resource.version)}${suffix}`
}

function _metricUrl(
  baseUrl: string,
  resource: TelMetricResource,
  suffix: string,
): string {
  return `${baseUrl}/${encodeURIComponent(resource.name)}/${encodeURIComponent(resource.env)}${suffix}`
}

function normalizeMiniTelemetryBaseUrl(url: string): string {
  const trimmed = url.endsWith('/') ? url.slice(0, -1) : url
  return trimmed.endsWith('/tel') ? trimmed : `${trimmed}/tel`
}

function formatDeployResource(resource: TelDeployResource): string {
  return [
    `service.name=${resource.name}`,
    `service.version=${resource.version}`,
    `deployment.environment=${resource.env}`,
    `origin=${resource.origin}`,
  ].join(' ')
}

function formatMetricResource(resource: TelMetricResource): string {
  return [
    `service.name=${resource.name}`,
    `deployment.environment=${resource.env}`,
    `origin=${resource.origin}`,
  ].join(' ')
}

function createSuccessfulTraceGuard(input: {
  deployResource: TelDeployResource
  requestedSampleRate: number
  dangerouslyAllowProdSuccessfulTraceSampling: boolean
  logPrefix: string
}) {
  const prod = isProdEnv(input.deployResource.env)
  const risky = prod && input.requestedSampleRate > 0
  const clamped = risky && !input.dangerouslyAllowProdSuccessfulTraceSampling
  let exported = 0
  let blocked = 0
  let lastWarnedAt = risky ? Date.now() : 0

  if (risky) {
    console.warn(
      successfulTraceWarningMessage({
        input,
        clamped,
        exported,
        blocked,
        recurring: false,
      }),
    )
  }

  return {
    effectiveSampleRate: clamped ? 0 : input.requestedSampleRate,
    recordSuccessTrace(exportedTrace: boolean): void {
      if (!risky) return
      if (exportedTrace) exported += 1
      else blocked += 1
      const now = Date.now()
      if (now - lastWarnedAt < SUCCESSFUL_TRACE_WARNING_INTERVAL_MS) return
      lastWarnedAt = now
      console.warn(
        successfulTraceWarningMessage({
          input,
          clamped,
          exported,
          blocked,
          recurring: true,
        }),
      )
    },
  }
}

function successfulTraceWarningMessage(input: {
  input: {
    deployResource: TelDeployResource
    requestedSampleRate: number
    dangerouslyAllowProdSuccessfulTraceSampling: boolean
    logPrefix: string
  }
  clamped: boolean
  exported: number
  blocked: number
  recurring: boolean
}): string {
  const { deployResource, requestedSampleRate, logPrefix } = input.input
  const action = input.clamped
    ? 'blocked production successful trace export'
    : 'dangerously allowed production successful trace export'
  const count = input.clamped
    ? `blocked=${input.blocked}`
    : `exported=${input.exported}`
  return [
    `${logPrefix} ${action}`,
    `resource=${deployResource.name}`,
    `env=${deployResource.env}`,
    `successfulTraceSampleRate=${requestedSampleRate}`,
    input.recurring ? count : '',
    'cost=every successful trace stored/billed',
    'incident=meter-values 4.65M spans ≈ 81% of prod telemetry from one service',
    input.clamped
      ? 'effectiveSuccessfulTraceSampleRate=0'
      : 'escape_hatch=dangerouslyAllowProdSuccessfulTraceSampling',
    'set successfulTraceSampleRate per env; only keep with dangerouslyAllowProdSuccessfulTraceSampling',
  ]
    .filter(Boolean)
    .join(' ')
}

function toMinimalSpan(span: SpanData): SpanData {
  const {
    input: _input,
    inputSize: _inputSize,
    output: _output,
    outputSize: _outputSize,
    ...minimal
  } = span
  return minimal
}

function enqueuePendingErrorGroupIds(
  recorder: ErrorTrackingHandler,
  batcher: Batcher<string>,
): void {
  for (const groupId of recorder.listPendingGroupIds()) {
    batcher.enqueue(groupId)
  }
}

function requireStorage(storage: TelStorage | undefined): TelStorage {
  if (!storage) throw new Error('HTTP exporter must be bound through root tel')
  return storage
}

function findErrorGroupIds(trace: TraceData): string[] {
  const ids = new Set<string>()
  visitTrace(trace, (span) => {
    if (span.fingerprint) ids.add(span.fingerprint)
    for (const log of span.logs) {
      if (log.fingerprint) ids.add(log.fingerprint)
    }
  })
  return [...ids]
}

function findPendingBoundaryGroupIds(
  pendingCreates: Map<string, PendingClientErrorCreate>,
  trace: TraceData,
): string[] {
  const ids = new Set<string>()
  visitTrace(trace, (span) => {
    for (const [groupId, create] of pendingCreates) {
      if (create.ready) continue
      const occurrence = create.change.occurrence
      if (
        occurrence.traceId === span.traceId ||
        occurrence.requestId === span.requestId ||
        occurrence.spanId === span.id
      ) {
        ids.add(groupId)
      }
    }
  })
  return [...ids]
}

export function pruneErrorBranches(trace: TraceData): TraceData {
  const children = trace.children
    .map((child) => pruneErrorBranches(child))
    .filter((child) => hasTraceErrorRef(child))
  return { ...trace, children }
}

function hasTraceErrorRef(trace: TraceData): boolean {
  if (trace.fingerprint !== undefined || trace.error !== undefined) return true
  if (
    trace.logs.some(
      (log) => log.fingerprint !== undefined || log.error !== undefined,
    )
  ) {
    return true
  }
  return trace.children.some((child) => hasTraceErrorRef(child))
}

function visitTrace(trace: TraceData, fn: (span: TraceData) => void): void {
  fn(trace)
  for (const child of trace.children) visitTrace(child, fn)
}

function getActors(
  actors: Map<string, { userIds: Set<string>; companyIds: Set<string> }>,
  groupId: string,
): { userIds: Set<string>; companyIds: Set<string> } {
  let existing = actors.get(groupId)
  if (!existing) {
    existing = { userIds: new Set(), companyIds: new Set() }
    actors.set(groupId, existing)
  }
  return existing
}

function addActor(set: Set<string>, value: string): string[] {
  if (set.has(value)) return []
  set.add(value)
  return [value]
}

function errorOccurrenceKey(occurrence: ClientErrorOccurrence): string {
  return [
    occurrence.groupId,
    occurrence.requestId ??
      occurrence.traceId ??
      occurrence.spanId ??
      occurrence.logId ??
      occurrence.timestamp,
  ].join(':')
}

function scopeErrorGroupId(groupId: string, scope: string | undefined): string {
  return scope ? `${scope}:${groupId}` : groupId
}

function cloneErrorGroup(group: ClientErrorGroup): ClientErrorGroup {
  return {
    ...group,
    groupingFrames: group.groupingFrames.map((frame) => ({ ...frame })),
  }
}

function cloneErrorGroupChange(
  change: ClientErrorGroupChange,
): ClientErrorGroupChange {
  return {
    ...change,
    userIds: [...change.userIds],
    companyIds: [...change.companyIds],
    occurrence: { ...change.occurrence },
  }
}
